import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Award, Star, Users, Phone, Mail } from 'lucide-react';
import ReviewsSection from '@/components/ReviewsSection';
import SEOHead from '@/components/SEOHead';
import { pageSeoData } from '@/lib/seo';
import { supabase } from '@/lib/customSupabaseClient';
import FleetImageOptimized from '@/components/FleetImageOptimized';
import ImagePreloader from '@/components/ImagePreloader';
import optimizedFleetService from '@/lib/optimizedFleetService';
import { FALLBACK_FLEET, VEHICLE_ORDER } from '@/constants/fleet';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Luxury car images collection
const luxuryCars = [
  {
    id: 1,
    mobile: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=768&h=432&fit=crop&auto=format&q=80',
    desktop: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=1920&h=1080&fit=crop&auto=format&q=80',
    alt: 'Luxury black Rolls-Royce Phantom with golden accents'
  },
  {
    id: 2,
    mobile: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=768&h=432&fit=crop&auto=format&q=80',
    desktop: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1920&h=1080&fit=crop&auto=format&q=80',
    alt: 'Luxury black Bentley Continental GT'
  },
  {
    id: 3,
    mobile: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=768&h=432&fit=crop&auto=format&q=80',
    desktop: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=1920&h=1080&fit=crop&auto=format&q=80',
    alt: 'Luxury black Mercedes-Benz S-Class'
  },
  {
    id: 4,
    mobile: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=768&h=432&fit=crop&auto=format&q=80',
    desktop: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=1920&h=1080&fit=crop&auto=format&q=80',
    alt: 'Luxury black BMW 7 Series'
  },
  {
    id: 5,
    mobile: 'https://images.unsplash.com/photo-1614200187522-5496ef2aa7a2?w=768&h=432&fit=crop&auto=format&q=80',
    desktop: 'https://images.unsplash.com/photo-1614200187522-5496ef2aa7a2?w=1920&h=1080&fit=crop&auto=format&q=80',
    alt: 'Luxury black Audi A8'
  },
  {
    id: 6,
    mobile: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=768&h=432&fit=crop&auto=format&q=80',
    desktop: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=1920&h=1080&fit=crop&auto=format&q=80',
    alt: 'Luxury black Maserati Quattroporte'
  }
];

const Home = () => {
  const [currentCar, setCurrentCar] = useState(luxuryCars[0]);
  const [fleet, setFleet] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(true);

  // Function to get random car
  const getRandomCar = () => {
    const randomIndex = Math.floor(Math.random() * luxuryCars.length);
    return luxuryCars[randomIndex];
  };

  // Fetch fleet data from database using optimized service
  const fetchFleet = async () => {
    try {
      setFleetLoading(true);
      const data = await optimizedFleetService.getVehicles();
      
      if (import.meta.env.DEV) {
        console.log('🔍 Raw fleet data from database:', data);
        console.log('🔍 Vehicle names in database:', data.map(v => ({ name: v.name, status: v.status })));
      }
      
      // Filter for active vehicles (include unset status as active)
      const activeFleet = data.filter(vehicle => {
        const status = (vehicle.status || 'Active').toString().toLowerCase();
        const isIncluded = status === 'active';
        if (import.meta.env.DEV) {
          console.log(`🔍 Vehicle "${vehicle.name}" (${vehicle.type}) - Status: "${vehicle.status}" - Included: ${isIncluded}`);
        }
        return isIncluded;
      });
      
      // Sort vehicles according to the defined order using imported constant
      const sortedFleet = activeFleet.sort((a, b) => {
        const aIndex = VEHICLE_ORDER.indexOf(a.name);
        const bIndex = VEHICLE_ORDER.indexOf(b.name);
        return aIndex - bIndex;
      });
      
      if (import.meta.env.DEV) {
        console.log('🔍 Active fleet after filtering:', activeFleet);
        console.log('🔍 Sorted fleet:', sortedFleet);
        console.log('🔍 Fleet count:', sortedFleet.length);
      }
      
      setFleet(sortedFleet);
    } catch (error) {
      console.error('Error fetching fleet:', error);
      setFleet([]);
    } finally {
      setFleetLoading(false);
    }
  };

  // Set up car rotation every 30 minutes
  useEffect(() => {
    // Set initial random car
    setCurrentCar(getRandomCar());

    // Set up interval to change car every 30 minutes (30 * 60 * 1000 ms)
    const interval = setInterval(() => {
      setCurrentCar(getRandomCar());
    }, 30 * 60 * 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // Fetch fleet data on component mount
  useEffect(() => {
    fetchFleet();
    
    // Listen for fleet data updates from admin
    const handleFleetUpdate = () => {
      console.log('🔄 Fleet data updated, refreshing Home page...');
      fetchFleet();
    };
    
    // Listen for services data updates from admin
    const handleServicesUpdate = () => {
      console.log('🔄 Services data updated, refreshing Home page...');
      // Note: Home page doesn't currently fetch services data, but this is ready for future use
    };
    
    window.addEventListener('fleetDataUpdated', handleFleetUpdate);
    window.addEventListener('servicesDataUpdated', handleServicesUpdate);
    
    return () => {
      window.removeEventListener('fleetDataUpdated', handleFleetUpdate);
      window.removeEventListener('servicesDataUpdated', handleServicesUpdate);
    };
  }, []);
  // Get critical images for preloading (only first 3 for performance)
  const criticalImages = useMemo(() => {
    const images = [];
    
    // Add fleet images (only first 3 for performance)
    if (fleet.length > 0) {
      fleet.slice(0, 3).forEach(vehicle => {
        if (vehicle.image_url && !vehicle.image_url.startsWith('data:')) {
          images.push(vehicle.image_url);
        }
      });
    } else {
      // Add fallback fleet images (only first 3)
      FALLBACK_FLEET.slice(0, 3).forEach(vehicle => {
        if (vehicle.image_url && !vehicle.image_url.startsWith('data:')) {
          images.push(vehicle.image_url);
        }
      });
    }
    
    return images;
  }, [fleet]);

  return (
    <>
      <SEOHead {...pageSeoData.home} />
      <ImagePreloader images={criticalImages} />
      <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.2 } } }}>
        
        <section className="relative h-[70vh] sm:h-[80vh] min-h-[400px] sm:min-h-[500px] flex items-center justify-center text-white text-center px-4 overflow-hidden">
          {/* Enhanced gradient overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/60 to-black/80 z-10"></div>
          
          {/* Subtle golden glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-gold/3 to-transparent z-15"></div>
          
          <picture>
            <source 
              media="(max-width: 768px)" 
              srcSet={currentCar.mobile} 
              type="image/webp" 
            />
            <source 
              media="(max-width: 1024px)" 
              srcSet={currentCar.mobile} 
              type="image/webp" 
            />
            <source 
              srcSet={currentCar.desktop} 
              type="image/webp" 
            />
            <img 
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000" 
              alt={currentCar.alt} 
              src={currentCar.mobile} 
              loading="eager"
              width="768"
              height="432"
              fetchpriority="high"
              sizes="(max-width: 768px) 768px, (max-width: 1024px) 1024px, 1920px"
            />
          </picture>
          
          <motion.div variants={fadeIn} className="relative z-20 max-w-4xl">
            {/* Enhanced title with golden accent */}
            <div className="relative">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif leading-tight tracking-wider text-white drop-shadow-2xl">
                <span className="bg-gradient-to-r from-white via-brand-gold via-yellow-300 to-white bg-clip-text text-transparent">
                  Driven By Excellence
                </span>
              </h1>
              {/* Enhanced decorative golden line */}
              <div className="mt-4 mx-auto w-24 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
            </div>
            
            <p className="mt-6 text-lg sm:text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed drop-shadow-2xl font-medium">
              At <span className="text-brand-gold font-bold bg-gradient-to-r from-brand-gold to-yellow-400 bg-clip-text text-transparent">Akiblackcar</span>, every trip is a premium experience. Receive a complimentary estimate tailored to your journey—no upfront payment or personal data required.
            </p>
            
            <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8">
              <Button asChild size="lg" className="bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold text-brand-dark hover:from-yellow-300 hover:via-yellow-500 hover:to-yellow-300 font-bold rounded-full px-12 sm:px-16 py-6 sm:py-8 text-xl sm:text-2xl transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-brand-gold/40 w-full sm:w-auto border-4 border-brand-gold/30 shadow-lg shadow-brand-gold/20">
                <Link to="/free-estimate">Get Your Free Estimate</Link>
              </Button>
            </div>
            
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12 text-xl sm:text-2xl">
              <a href="tel:7038431837" className="flex items-center gap-4 hover:text-brand-gold transition-all duration-300 hover:scale-110 group">
                <div className="p-3 rounded-full bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                  <Phone className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <span className="font-semibold">Call Us</span>
              </a>
              <a href="mailto:Akiblackcar@gmail.com" className="flex items-center gap-4 hover:text-brand-gold transition-all duration-300 hover:scale-110 group">
                <div className="p-3 rounded-full bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                  <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <span className="font-semibold">Email Us</span>
              </a>
            </div>
          </motion.div>
        </section>

        <section className="py-20 sm:py-24 lg:py-28 bg-gradient-to-br from-gray-900 via-brand-dark to-gray-800 relative overflow-hidden">
          {/* Enhanced decorative golden elements */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          <div className="absolute bottom-0 right-0 w-full h-2 bg-gradient-to-l from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          
          {/* Additional luxury lighting effects */}
          <div className="absolute top-1/4 left-0 w-64 h-64 bg-brand-gold/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-brand-gold/5 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div variants={fadeIn} className="text-center max-w-4xl mx-auto">
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold bg-clip-text text-transparent">
                  The Akiblackcar Promise
                </h2>
                {/* Decorative golden line */}
                <div className="mt-6 mx-auto w-32 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
              </div>
              <p className="mt-8 text-lg sm:text-xl text-white leading-relaxed drop-shadow-lg">
                With over a decade of dedicated service, we've built a reputation for excellence. Our commitment to punctuality, professionalism, and passenger satisfaction is unmatched.
              </p>
            </motion.div>
            
            <div className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-12 text-center">
              <motion.div variants={fadeIn} className="group">
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group-hover:scale-110 hover:bg-gradient-to-br hover:from-gray-700 hover:via-brand-charcoal hover:to-gray-600">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-20 h-20 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20 group-hover:shadow-xl group-hover:shadow-brand-gold/30">
                      <Award className="h-10 w-10 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-8 text-2xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300">10+ Years of Experience</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white group-hover:text-gray-100 transition-colors duration-300 text-lg drop-shadow-sm">Perfecting luxury travel in the DMV area for over a decade.</p>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div variants={fadeIn} className="group">
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group-hover:scale-110 hover:bg-gradient-to-br hover:from-gray-700 hover:via-brand-charcoal hover:to-gray-600">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-20 h-20 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20 group-hover:shadow-xl group-hover:shadow-brand-gold/30">
                      <Star className="h-10 w-10 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-8 text-2xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300">High Customer Satisfaction</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white group-hover:text-gray-100 transition-colors duration-300 text-lg drop-shadow-sm">Our top priority is your comfort and satisfaction on every trip.</p>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div variants={fadeIn} className="group">
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group-hover:scale-110 hover:bg-gradient-to-br hover:from-gray-700 hover:via-brand-charcoal hover:to-gray-600">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-20 h-20 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20 group-hover:shadow-xl group-hover:shadow-brand-gold/30">
                      <Users className="h-10 w-10 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-8 text-2xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300">Professional Chauffeurs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white group-hover:text-gray-100 transition-colors duration-300 text-lg drop-shadow-sm">Courteous, discreet, and highly-trained drivers at your service.</p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-24 lg:py-28 bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-900 relative overflow-hidden">
          {/* Enhanced decorative golden elements */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          <div className="absolute bottom-0 right-0 w-full h-2 bg-gradient-to-l from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          
          {/* Additional luxury lighting effects */}
          <div className="absolute top-1/3 left-0 w-80 h-80 bg-brand-gold/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-0 w-80 h-80 bg-brand-gold/5 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div variants={fadeIn} className="text-center max-w-4xl mx-auto">
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold bg-clip-text text-transparent">
                  Our Luxury Fleet
                </h2>
                {/* Decorative golden line */}
                <div className="mt-6 mx-auto w-32 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
              </div>
              <p className="mt-8 text-lg sm:text-xl text-white leading-relaxed drop-shadow-lg">
                A vehicle for every occasion. Select from our meticulously maintained fleet of high-end vehicles.
              </p>
            </motion.div>
            <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 sm:gap-10 lg:gap-12">
              {fleetLoading ? (
                // Loading state - show 5 vehicles
                Array.from({ length: 5 }).map((_, index) => (
                  <motion.div key={index} variants={fadeIn} className="group">
                    <Card className="bg-gradient-to-br from-gray-700 via-brand-dark to-gray-800 border-brand-gold/40 overflow-hidden">
                      <div className="overflow-hidden h-56 relative">
                        <div className="w-full h-full bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 flex items-center justify-center">
                          <div className="text-center text-white">
                            <div className="animate-pulse text-4xl mb-2">⏳</div>
                            <div className="text-lg font-semibold">Loading...</div>
                          </div>
                        </div>
                      </div>
                      <CardHeader className="pb-6">
                        <CardTitle className="text-xl font-serif text-center text-white">Loading Vehicle...</CardTitle>
                      </CardHeader>
                    </Card>
                  </motion.div>
                ))
              ) : fleet.length > 0 ? (
                // Display fleet from database
                fleet.map((vehicle, index) => (
                  <motion.div key={vehicle.id} variants={fadeIn} className="group">
                    <Link to="/free-estimate">
                      <Card className="bg-gradient-to-br from-gray-700 via-brand-dark to-gray-800 border-brand-gold/40 overflow-hidden group-hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group-hover:scale-110 cursor-pointer">
                        <div className="overflow-hidden h-56 relative">
                        <FleetImageOptimized
                          src={vehicle.image_url}
                          alt={vehicle.name || vehicle.type}
                          vehicleType={vehicle.type}
                          vehicleName={vehicle.name}
                          className="w-full h-full object-cover"
                          fallbackSrc={vehicle.image_url_fallback}
                          priority={index < 3} // First 3 images are priority
                        />
                          {/* Enhanced golden overlay on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-brand-gold/30 via-brand-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          {/* Book now overlay */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-brand-gold/90 text-brand-dark px-6 py-3 rounded-full font-bold text-lg shadow-lg">
                              Get Your Free Estimate
                            </div>
                          </div>
                        </div>
                        <CardHeader className="pb-6">
                          <CardTitle className="text-xl font-serif text-center text-white group-hover:text-brand-gold transition-colors duration-300">
                            {vehicle.name || vehicle.type}
                          </CardTitle>
                          {vehicle.capacity && (
                            <div className="text-center text-sm text-gray-300">
                              Capacity: {vehicle.capacity} passengers
                            </div>
                          )}
                        </CardHeader>
                      </Card>
                    </Link>
                  </motion.div>
                ))
              ) : (
                // Fallback when no fleet data is available - use local fleet constants
                FALLBACK_FLEET.map((vehicle, index) => {
                  
                  return (
                    <motion.div key={index} variants={fadeIn} className="group">
                      <Link to="/free-estimate">
                        <Card className="bg-gradient-to-br from-gray-700 via-brand-dark to-gray-800 border-brand-gold/40 overflow-hidden group-hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group-hover:scale-110 cursor-pointer">
                          <div className="overflow-hidden h-56 relative">
                        <FleetImageOptimized
                          src={vehicle.image_url}
                          alt={vehicle.alt || vehicle.name}
                          vehicleType={vehicle.type}
                          vehicleName={vehicle.name}
                          className="w-full h-full object-cover"
                          priority={index < 3} // First 3 images are priority
                        />
                            {/* Enhanced golden overlay on hover */}
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-gold/30 via-brand-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            {/* Book now overlay */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="bg-brand-gold/90 text-brand-dark px-6 py-3 rounded-full font-bold text-lg shadow-lg">
                                Get Your Free Estimate
                              </div>
                            </div>
                          </div>
                          <CardHeader className="pb-6">
                            <CardTitle className="text-xl font-serif text-center text-white group-hover:text-brand-gold transition-colors duration-300">
                              {vehicle.name}
                            </CardTitle>
                            <div className="text-center text-sm text-gray-300">
                              Capacity: {vehicle.capacity} passengers
                            </div>
                          </CardHeader>
                        </Card>
                      </Link>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>
        </section>

        <ReviewsSection />

        <section className="py-20 sm:py-24 lg:py-28 bg-gradient-to-br from-brand-gold via-yellow-400 to-yellow-500 relative overflow-hidden">
          {/* Enhanced decorative elements */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-dark to-transparent shadow-lg shadow-brand-dark/30"></div>
          <div className="absolute bottom-0 right-0 w-full h-2 bg-gradient-to-l from-transparent via-brand-dark to-transparent shadow-lg shadow-brand-dark/30"></div>
          
          {/* Additional luxury lighting effects */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <motion.div variants={fadeIn}>
              <div className="relative">
                <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold font-serif text-brand-dark drop-shadow-xl">
                  Ready for an Unforgettable Experience?
                </h2>
                {/* Enhanced decorative line */}
                <div className="mt-8 mx-auto w-40 h-2 bg-gradient-to-r from-transparent via-brand-dark to-transparent shadow-lg shadow-brand-dark/30"></div>
                <div className="mt-2 mx-auto w-24 h-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
              </div>
              <p className="mt-10 text-xl sm:text-2xl text-brand-dark/95 max-w-4xl mx-auto leading-relaxed font-semibold">
                Book your ride today and discover the pinnacle of luxury transportation.
              </p>
              <div className="mt-12 sm:mt-16">
                <Button asChild size="lg" className="bg-gradient-to-r from-brand-dark via-black to-brand-dark text-white hover:from-gray-700 hover:via-gray-800 hover:to-gray-700 font-bold rounded-full px-16 sm:px-20 py-8 sm:py-10 text-xl sm:text-2xl transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-brand-dark/40 border-4 border-brand-dark/30 shadow-xl shadow-brand-dark/20">
                  <Link to="/free-estimate">Get Your Free Estimate</Link>
                </Button>
              </div>
              
              {/* Enhanced contact options */}
              <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12 text-brand-dark">
                <a href="tel:7038431837" className="flex items-center gap-4 hover:text-brand-dark/80 transition-all duration-300 hover:scale-110 group">
                  <div className="p-4 rounded-full bg-gradient-to-br from-brand-dark/30 to-black/30 group-hover:from-brand-dark/50 group-hover:to-black/50 transition-all duration-300 shadow-lg shadow-brand-dark/20 group-hover:shadow-xl group-hover:shadow-brand-dark/30">
                    <Phone className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <span className="font-bold text-lg sm:text-xl">Call (703) 843-1837</span>
                </a>
                <a href="mailto:Akiblackcar@gmail.com" className="flex items-center gap-4 hover:text-brand-dark/80 transition-all duration-300 hover:scale-110 group">
                  <div className="p-4 rounded-full bg-gradient-to-br from-brand-dark/30 to-black/30 group-hover:from-brand-dark/50 group-hover:to-black/50 transition-all duration-300 shadow-lg shadow-brand-dark/20 group-hover:shadow-xl group-hover:shadow-brand-dark/30">
                    <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <span className="font-bold text-lg sm:text-xl">Email Us</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

      </motion.div>
    </>
  );
};

export default Home;